/**
 * Wiki Intelligence Dashboard
 * Admin page for reviewing auto-generated wiki entries.
 * Uses AdminLayout (logo + "Admin Console" header) to match SEAL UI.
 */

import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
// Admin-plane API — sends adminAccessToken + x-2fa-session. WikiDashboard
// is a pure admin page; the underlying /api/wiki/admin/* proxy routes are
// authenticateAdmin-gated. Regular user JWT would 401.
import adminApi from '../../services/adminApi';

const QUALITY_CONFIG = {
  high:   { label: 'High',   color: '#16a34a', bg: '#f0fdf4', icon: '🟢' },
  medium: { label: 'Medium', color: '#ca8a04', bg: '#fefce8', icon: '🟡' },
  low:    { label: 'Low',    color: '#dc2626', bg: '#fef2f2', icon: '🔴' }
};

const getQuality = (score) => {
  if (score >= 0.90) return QUALITY_CONFIG.high;
  if (score >= 0.80) return QUALITY_CONFIG.medium;
  return QUALITY_CONFIG.low;
};

const WikiDashboard = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const [pending, setPending] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    if (activeTab === 'pending') fetchPending();
    if (activeTab === 'metrics') fetchMetrics();
  }, [activeTab]);

  const fetchPending = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get('/api/wiki/admin/pending');
      setPending(res.data?.entries || []);
    } catch (e) {
      console.error('Wiki pending error:', e);
      setPending([]);
    } finally { setLoading(false); }
  };

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get('/api/wiki/admin/metrics');
      setMetrics(res.data?.metrics || null);
    } catch (e) {
      console.error('Wiki metrics error:', e);
      setMetrics(null);
    } finally { setLoading(false); }
  };

  const handleAction = async (id, action) => {
    setActionLoading(prev => ({...prev, [id]: action}));
    try {
      if (action === 'approve') {
        await adminApi.post(`/api/wiki/admin/approve/${id}`);
      } else if (action === 'reject') {
        await adminApi.post(`/api/wiki/admin/reject/${id}`);
      } else if (action === 'keep_user') {
        await adminApi.post(`/api/wiki/admin/reject/${id}`);
      }
      fetchPending();
    } catch (e) {
      console.error('Action failed:', e);
    } finally {
      setActionLoading(prev => {
        const n = {...prev};
        delete n[id];
        return n;
      });
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-IN', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading && pending.length === 0 && !metrics) {
    return (
      <AdminLayout showBackButton backPath="/admin/dashboard">
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: 20 }}>
          <div style={{
            textAlign: 'center', padding: 60, color: '#94a3b8'
          }}>
            Loading Wiki Dashboard...
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout showBackButton backPath="/admin/dashboard">
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: 20 }}>

        {/* Top Bar — matches SEAL .seal-topbar */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
          flexWrap: 'wrap',
          gap: 12
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 style={{
              margin: 0, fontSize: 22, fontWeight: 700, color: '#1e293b',
              display: 'flex', alignItems: 'center', gap: 8
            }}>
              <span style={{ fontSize: 24 }}>📚</span>
              Wiki Intelligence Dashboard
            </h1>
            {pending.length > 0 && (
              <span style={{
                background: '#fefce8', color: '#ca8a04',
                padding: '4px 12px', borderRadius: 20,
                fontSize: 13, fontWeight: 600
              }}>
                {pending.length} pending
              </span>
            )}
          </div>
          <button
            onClick={() => { fetchPending(); fetchMetrics(); }}
            style={{
              padding: '8px 16px', background: '#fff',
              border: '1px solid #e5e7eb', borderRadius: 8,
              cursor: 'pointer', fontSize: 13, color: '#64748b'
            }}
          >
            ⟳ Refresh
          </button>
        </div>

        {/* Stats Cards — matches SEAL .seal-metrics-row */}
        {metrics && (
          <div style={{
            background: '#fff', border: '1px solid #e5e7eb',
            borderRadius: 12, padding: 20, marginBottom: 24
          }}>
            <h2 style={{
              margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: '#1e293b'
            }}>
              Queue Overview
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
              gap: 12
            }}>
              {[
                { label: 'Total Entries', value: metrics.total, color: '#1e293b', highlight: false },
                { label: 'Global (Live)', value: metrics.global, color: '#16a34a', highlight: false },
                { label: 'Pending Review', value: metrics.pending, color: metrics.pending > 0 ? '#ca8a04' : '#16a34a', highlight: metrics.pending > 0 },
                { label: 'User Private', value: metrics.user, color: '#7c3aed', highlight: false },
                { label: 'Auto-Generated', value: metrics.auto_generated, color: '#0891b2', highlight: false },
                { label: 'Scout Agent', value: metrics.scout_agent, color: '#7c3aed', highlight: false },
                { label: 'Avg Quality', value: Math.round((metrics.avg_quality_score || 0) * 100) + '%', color: '#16a34a', highlight: false }
              ].map((stat, i) => (
                <div key={i} style={{
                  border: stat.highlight ? `2px solid ${stat.color}` : '1px solid #e5e7eb',
                  borderRadius: 10, padding: 16, textAlign: 'center',
                  background: stat.highlight ? stat.color + '08' : '#fff'
                }}>
                  <div style={{ fontSize: 26, fontWeight: 700, color: stat.color }}>
                    {stat.value}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs — matches SEAL tab style */}
        <div style={{
          display: 'flex', gap: 4, marginBottom: 24,
          borderBottom: '2px solid #e5e7eb'
        }}>
          {[
            { key: 'pending', label: 'Pending Review', icon: '⏳', badge: pending.length },
            { key: 'metrics', label: 'Metrics', icon: '📊' }
          ].map(tab => (
            <button key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '10px 20px', border: 'none', background: 'none',
                cursor: 'pointer', fontSize: 14,
                fontWeight: activeTab === tab.key ? 600 : 400,
                color: activeTab === tab.key ? '#7c3aed' : '#6b7280',
                borderBottom: activeTab === tab.key
                  ? '2px solid #7c3aed' : '2px solid transparent',
                marginBottom: -2,
                display: 'flex', alignItems: 'center', gap: 6
              }}
            >
              {tab.icon} {tab.label}
              {tab.badge > 0 && (
                <span style={{
                  background: '#7c3aed', color: '#fff',
                  borderRadius: 12, padding: '2px 8px', fontSize: 11
                }}>
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ═══ PENDING REVIEW TAB ═══ */}
        {activeTab === 'pending' && (
          <div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
                Loading...
              </div>
            ) : pending.length === 0 ? (
              <div style={{
                background: '#fff', borderRadius: 12, padding: 60,
                textAlign: 'center', border: '1px solid #e5e7eb'
              }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
                <h3 style={{ color: '#1e293b', marginBottom: 8 }}>
                  All clear — nothing to review
                </h3>
                <p style={{ color: '#94a3b8', margin: 0 }}>
                  High quality answers will appear here automatically
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {pending.map(entry => {
                  const q = getQuality(entry.quality_score || 0);
                  return (
                    <div key={entry.id} style={{
                      background: '#fff',
                      border: '1px solid #e5e7eb',
                      borderLeft: `4px solid ${q.color}`,
                      borderRadius: 10,
                      padding: 16
                    }}>
                      {/* Card header — matches .candidate-card-header */}
                      <div style={{
                        display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', marginBottom: 8,
                        flexWrap: 'wrap', gap: 8
                      }}>
                        <div style={{
                          display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap'
                        }}>
                          <span style={{
                            fontSize: 12, padding: '2px 10px', borderRadius: 10,
                            background: '#f5f3ff', color: '#7c3aed', fontWeight: 600
                          }}>
                            📚 Knowledge Entry
                          </span>
                          <span style={{
                            fontSize: 12, padding: '2px 10px', borderRadius: 10,
                            background: q.bg, color: q.color, fontWeight: 600
                          }}>
                            {q.icon} {q.label}
                          </span>
                          <span style={{
                            fontSize: 12, padding: '2px 10px', borderRadius: 10,
                            background: '#fefce8', color: '#ca8a04'
                          }}>
                            Pending
                          </span>
                        </div>
                        <span style={{ fontSize: 13, color: '#94a3b8' }}>
                          {formatTime(entry.created_at)}
                        </span>
                      </div>

                      {/* Title — matches .candidate-title */}
                      <h3 style={{
                        margin: '0 0 8px', fontSize: 15,
                        fontWeight: 600, color: '#1e293b'
                      }}>
                        {entry.topic}
                      </h3>

                      {/* Content preview — matches .candidate-desc */}
                      <p style={{
                        color: '#475569', fontSize: 14, lineHeight: 1.6,
                        margin: '0 0 12px', background: '#f9fafb',
                        padding: 12, borderRadius: 8
                      }}>
                        {(entry.content || entry.summary || '').substring(0, 400)}
                        {(entry.content || '').length > 400 ? '...' : ''}
                      </p>

                      {/* Footer meta — matches .candidate-card-footer */}
                      <div style={{
                        display: 'flex', gap: 16, marginBottom: 12,
                        fontSize: 13, color: '#6b7280', flexWrap: 'wrap'
                      }}>
                        <span>
                          Domain: <strong>{entry.domain?.toUpperCase()}</strong>
                        </span>
                        <span>
                          Quality: <strong style={{ color: q.color }}>
                            {Math.round((entry.quality_score || 0) * 100)}%
                          </strong>
                        </span>
                        <span>
                          Source: <strong>{entry.source_type}</strong>
                        </span>
                      </div>

                      {/* Tags */}
                      {(entry.tags || []).length > 0 && (
                        <div style={{
                          display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12
                        }}>
                          {(entry.tags || []).map((tag, i) => (
                            <span key={i} style={{
                              padding: '2px 8px', borderRadius: 12,
                              background: '#f3f4f6', color: '#6b7280', fontSize: 12
                            }}>
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Action buttons — matches SEAL .review-actions */}
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleAction(entry.id, 'approve')}
                          disabled={!!actionLoading[entry.id]}
                          style={{
                            padding: '8px 16px', background: '#16a34a',
                            color: '#fff', border: 'none', borderRadius: 8,
                            cursor: 'pointer', fontSize: 13, fontWeight: 500,
                            opacity: actionLoading[entry.id] ? 0.7 : 1
                          }}
                        >
                          {actionLoading[entry.id] === 'approve' ? 'Approving...' : '✅ Approve Global'}
                        </button>
                        <button
                          onClick={() => handleAction(entry.id, 'keep_user')}
                          disabled={!!actionLoading[entry.id]}
                          style={{
                            padding: '8px 16px', background: '#7c3aed',
                            color: '#fff', border: 'none', borderRadius: 8,
                            cursor: 'pointer', fontSize: 13, fontWeight: 500,
                            opacity: actionLoading[entry.id] ? 0.7 : 1
                          }}
                        >
                          👤 Keep User-Only
                        </button>
                        <button
                          onClick={() => handleAction(entry.id, 'reject')}
                          disabled={!!actionLoading[entry.id]}
                          style={{
                            padding: '8px 16px', background: '#fff',
                            color: '#dc2626', border: '1px solid #dc2626',
                            borderRadius: 8, cursor: 'pointer',
                            fontSize: 13, fontWeight: 500,
                            opacity: actionLoading[entry.id] ? 0.7 : 1
                          }}
                        >
                          {actionLoading[entry.id] === 'reject' ? 'Rejecting...' : '❌ Reject'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ═══ METRICS TAB ═══ */}
        {activeTab === 'metrics' && (
          <div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
                Loading metrics...
              </div>
            ) : !metrics ? (
              <div style={{
                background: '#fff', borderRadius: 12, padding: 60,
                textAlign: 'center', border: '1px solid #e5e7eb'
              }}>
                <p style={{ color: '#94a3b8' }}>No metrics available yet</p>
              </div>
            ) : (
              <div>
                {/* By Domain — matches SEAL .seal-section */}
                <div style={{
                  background: '#fff', border: '1px solid #e5e7eb',
                  borderRadius: 12, padding: 20
                }}>
                  <h3 style={{
                    margin: '0 0 16px', fontSize: 16, color: '#1e293b'
                  }}>
                    Entries by Domain
                  </h3>
                  {Object.entries(metrics.by_domain || {}).map(([domain, count]) => (
                    <div key={domain} style={{
                      display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', padding: '10px 0',
                      borderBottom: '1px solid #f1f5f9'
                    }}>
                      <span style={{
                        fontSize: 14, textTransform: 'capitalize', color: '#475569'
                      }}>
                        {domain}
                      </span>
                      <span style={{ fontWeight: 600, color: '#7c3aed' }}>
                        {count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default WikiDashboard;
